<template>
  <b-card no-body>
    <h4 class="font-weight-bold p-1" style="border-bottom: 1px solid #e5e7eb">
      Promotions
    </h4>
    <b-card-body>
      <b-row>
        <b-col
          v-for="promo in promos"
          :key="promo.field"
          cols="12"
          lg="4"
          class="mb-2"
        >
          <div class="promo-block h-100 p-1">
            <div class="d-flex align-items-center justify-content-between mb-1">
              <h5 class="font-weight-bolder mb-0">{{ promo.label }}</h5>
              <div class="d-flex align-items-center ml-1" style="gap: 10px">
                <label class="font-weight-bold mb-0">Enabled</label>
                <b-form-checkbox v-model="form[promo.field].enabled" switch />
              </div>
            </div>

            <small
              v-if="!form[promo.field].enabled"
              class="text-muted d-block mb-1"
              >Off for this player. Turn on to edit the values.</small
            >
            <fieldset :disabled="!form[promo.field].enabled">
              <b-row>
                <b-col cols="12" class="mb-1">
                  <div class="d-flex align-items-center" style="gap: 10px">
                    <b-form-checkbox
                      v-model="form[promo.field].isVisible"
                      :disabled="!form[promo.field].enabled"
                      switch
                    />
                    <label class="font-weight-bold mb-0">Visible</label>
                  </div>
                  <small class="text-muted"
                    >Off: hidden and claim blocked</small
                  >
                </b-col>
                <b-col cols="12" class="mb-1">
                  <div class="d-flex align-items-center" style="gap: 10px">
                    <b-form-checkbox
                      v-model="form[promo.field].isClaimAllowed"
                      :disabled="
                        !form[promo.field].enabled ||
                        !form[promo.field].isVisible
                      "
                      switch
                    />
                    <label class="font-weight-bold mb-0">{{
                      promo.claimLabel
                    }}</label>
                  </div>
                  <small class="text-muted">{{
                    form[promo.field].isVisible
                      ? "Off: progress stays visible but claim is blocked"
                      : "Only applies when Visible is on"
                  }}</small>
                </b-col>
              </b-row>
              <b-row>
                <b-col
                  v-for="input in promo.inputs"
                  :key="input.key"
                  cols="12"
                  sm="6"
                  class="mb-1"
                >
                  <label class="font-weight-bold">{{ input.label }}</label>
                  <b-input-group :append="input.unit">
                    <b-form-input
                      v-model.number="form[promo.field][input.key]"
                      type="number"
                      :step="input.integer ? 1 : 'any'"
                      :state="fieldValid(promo.field, input.key) ? null : false"
                      :no-wheel="true"
                    />
                  </b-input-group>
                  <small
                    v-if="!fieldValid(promo.field, input.key)"
                    class="text-danger"
                    >{{ input.hint }}</small
                  >
                </b-col>
              </b-row>
            </fieldset>
          </div>
        </b-col>
        <b-col cols="12" lg="4" class="mb-2">
          <div class="promo-block h-100 p-1">
            <div class="d-flex align-items-center justify-content-between mb-1">
              <h5 class="font-weight-bolder mb-0">Deposit Cashback 10/40</h5>
              <div class="d-flex align-items-center ml-1" style="gap: 10px">
                <label class="font-weight-bold mb-0">Enabled</label>
                <b-form-checkbox v-model="cashback.enabled" switch />
              </div>
            </div>
            <small class="text-muted d-block mb-1">{{
              cashback.enabled
                ? "Re-deposit of withdrawn amount gets Low %, the rest gets High %"
                : "Off: every deposit gets Low %"
            }}</small>
            <b-row>
              <b-col cols="12" sm="6" class="mb-1">
                <label class="font-weight-bold">Low %</label>
                <b-input-group append="%">
                  <b-form-input
                    :value="cashback.low"
                    type="number"
                    step="any"
                    :state="cashbackValid.low ? null : false"
                    :no-wheel="true"
                    @input="cashback.low = limitTwoDecimals($event)"
                  />
                </b-input-group>
                <small v-if="!cashbackValid.low" class="text-danger"
                  >0 – 999.99</small
                >
              </b-col>
              <b-col cols="12" sm="6" class="mb-1">
                <label class="font-weight-bold">High %</label>
                <b-input-group append="%">
                  <b-form-input
                    :value="cashback.high"
                    type="number"
                    step="any"
                    :disabled="!cashback.enabled"
                    :state="cashbackValid.high ? null : false"
                    :no-wheel="true"
                    @input="cashback.high = limitTwoDecimals($event)"
                  />
                </b-input-group>
                <small v-if="!cashbackValid.high" class="text-danger"
                  >0 – 999.99</small
                >
              </b-col>
            </b-row>
          </div>
        </b-col>
      </b-row>

      <div class="d-flex justify-content-end">
        <b-button
          size="md"
          variant="primary"
          class="d-flex align-items-center justify-content-center"
          :disabled="saving || !isDirty || !formValid"
          @click="handleSave"
        >
          <b-spinner v-if="saving" small />
          <feather-icon v-else size="20" icon="SaveIcon" />
          <span class="ml-1 text-lg text-white">Save changes</span>
        </b-button>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BButton,
  BCard,
  BCardBody,
  BCol,
  BFormCheckbox,
  BFormInput,
  BInputGroup,
  BRow,
  BSpinner,
} from "bootstrap-vue";
import { mapActions } from "vuex";
import { objectProp } from "@/helpers/props";
import useToastHandler from "@/services/toastHandler";

const isNum = (v) => typeof v === "number" && !Number.isNaN(v);
const isPositive = (v) => isNum(v) && v > 0;
const isBonusPercent = (v) => isNum(v) && v >= 0 && v <= 999.99;

// values used when a promo is turned on for a player who has none yet
const PROMOS = [
  {
    field: "depositMatchConfig",
    label: "Net Position Deposit Match",
    claimLabel: "Allow Deposit Match Opt In",
    defaults: {
      matchPercent: 100,
      maxBonus: 10000,
      minBalance: 1,
      optInsPerPhase: 1,
      lossThreshold: 50000,
    },
    inputs: [
      {
        key: "matchPercent",
        label: "Match %",
        unit: "%",
        hint: "1 – 100",
        valid: (v) => isPositive(v) && v <= 100,
      },
      {
        key: "maxBonus",
        label: "Max bonus",
        unit: "USDT",
        hint: "Must be greater than 0",
        valid: isPositive,
      },
      {
        key: "minBalance",
        label: "Opt in when balance below",
        unit: "USDT",
        hint: "Must be 0 or more",
        valid: (v) => isNum(v) && v >= 0,
      },
      {
        key: "lossThreshold",
        label: "Opt in every loss of",
        unit: "USDT",
        hint: "Must be greater than 0",
        valid: isPositive,
      },
    ],
  },
  {
    field: "bonusCreditConfig",
    label: "Advanced Rebate Bucket (Bonus Credit)",
    claimLabel: "Allow Draw",
    defaults: {
      creditLimit: 25000,
      maxPerClaim: 5000,
      minPerClaim: 1,
      minBalance: 1,
    },
    // backend rule: creditLimit >= maxPerClaim >= minPerClaim > 0
    inputs: [
      {
        key: "creditLimit",
        label: "Credit limit",
        unit: "USDT",
        hint: "Must be at least Max per draw",
        valid: (v, c) => isNum(v) && v >= c.maxPerClaim,
      },
      {
        key: "minPerClaim",
        label: "Min per draw",
        unit: "USDT",
        hint: "Must be greater than 0",
        valid: isPositive,
      },
      {
        key: "maxPerClaim",
        label: "Max per draw",
        unit: "USDT",
        hint: "Must be at least Min per draw",
        valid: (v, c) => isNum(v) && v >= c.minPerClaim,
      },
      {
        key: "minBalance",
        label: "Draw when balance below",
        unit: "USDT",
        hint: "Must be 0 or more",
        valid: (v) => isNum(v) && v >= 0,
      },
    ],
  },
];

const toForm = (promo, config) => ({
  enabled: !!config,
  isVisible: config ? !!config.isVisible : true,
  isClaimAllowed: config ? !!config.isClaimAllowed : true,
  ...promo.defaults,
  ...(config
    ? Object.fromEntries(
        Object.keys(promo.defaults).map((k) => [
          k,
          config[k] ?? promo.defaults[k],
        ])
      )
    : {}),
});

export default {
  components: {
    BButton,
    BCard,
    BCardBody,
    BCol,
    BFormCheckbox,
    BFormInput,
    BInputGroup,
    BRow,
    BSpinner,
  },
  props: {
    userData: objectProp(),
  },
  data() {
    return {
      promos: PROMOS,
      form: {},
      initialPayload: {},
      // numberOfBonusCashForDeposit(High) are top-level user fields, not a promo config block
      cashback: { enabled: false, low: null, high: null },
      initialCashback: {},
      saving: false,
    };
  },
  computed: {
    // null = promo off; otherwise the whole block, as BE replaces it entirely
    payloads() {
      return Object.fromEntries(
        PROMOS.map(({ field, defaults }) => {
          const f = this.form[field];
          if (!f || !f.enabled) return [field, null];
          return [
            field,
            {
              isVisible: !!f.isVisible,
              isClaimAllowed: !!f.isClaimAllowed,
              ...Object.fromEntries(
                Object.keys(defaults).map((k) => [
                  k,
                  f[k] === "" || f[k] === null ? NaN : Number(f[k]),
                ])
              ),
            },
          ];
        })
      );
    },
    // high null on BE = 10/40 off; empty low is left untouched like before
    cashbackPayload() {
      const { enabled, low, high } = this.cashback;
      return {
        numberOfBonusCashForDeposit:
          low === "" || low === null ? null : Number(low),
        numberOfBonusCashForDepositHigh: enabled
          ? high === "" || high === null
            ? NaN
            : Number(high)
          : null,
      };
    },
    cashbackValid() {
      const {
        numberOfBonusCashForDeposit: low,
        numberOfBonusCashForDepositHigh: high,
      } = this.cashbackPayload;
      return {
        low: low === null || isBonusPercent(low),
        high: !this.cashback.enabled || isBonusPercent(high),
      };
    },
    changed() {
      const out = Object.fromEntries(
        Object.entries(this.payloads).filter(
          ([field, value]) =>
            JSON.stringify(value) !== JSON.stringify(this.initialPayload[field])
        )
      );
      Object.entries(this.cashbackPayload).forEach(([k, v]) => {
        if (k === "numberOfBonusCashForDeposit" && v === null) return;
        if (v !== this.initialCashback[k]) out[k] = v;
      });
      return out;
    },
    isDirty() {
      return Object.keys(this.changed).length > 0;
    },
    formValid() {
      if (!this.cashbackValid.low || !this.cashbackValid.high) return false;
      return PROMOS.every(
        (promo) =>
          !this.form[promo.field]?.enabled ||
          promo.inputs.every((input) => this.fieldValid(promo.field, input.key))
      );
    },
  },
  created() {
    this.resetForm();
  },
  methods: {
    ...mapActions("user/detail", ["updateUserAdvance"]),
    resetForm() {
      const user = this.userData?.user || {};
      this.form = Object.fromEntries(
        PROMOS.map((promo) => [promo.field, toForm(promo, user[promo.field])])
      );
      this.initialPayload = JSON.parse(JSON.stringify(this.payloads));
      this.cashback = {
        enabled: user.numberOfBonusCashForDepositHigh != null,
        low: user.numberOfBonusCashForDeposit ?? null,
        high: user.numberOfBonusCashForDepositHigh ?? null,
      };
      this.initialCashback = { ...this.cashbackPayload };
    },
    limitTwoDecimals(value) {
      if (value === "" || value === null || value === undefined) return null;
      const str = String(value);
      if (str.includes(".")) {
        const [intPart, decPart] = str.split(".");
        if (decPart.length > 2) return `${intPart}.${decPart.slice(0, 2)}`;
      }
      return value;
    },
    fieldValid(field, key) {
      const promo = PROMOS.find((p) => p.field === field);
      const input = promo.inputs.find((i) => i.key === key);
      const values = this.payloads[field];
      return !values || input.valid(values[key], values);
    },
    async handleSave() {
      if (!this.formValid || !this.isDirty) return;
      // default msgBox body text is white on this theme, so set it explicitly
      const message = this.$createElement(
        "div",
        { style: { color: "#333", fontWeight: 500 } },
        "Are you sure you want to update promotions for this user?"
      );
      const confirmed = await this.$bvModal.msgBoxConfirm(message, {
        title: "Promotions",
        okTitle: "Confirm",
        cancelTitle: "Cancel",
        centered: true,
      });
      if (!confirmed) return;
      this.saving = true;
      try {
        const res = await this.updateUserAdvance({
          userId: this.$route.params.id,
          ...this.changed,
        });
        if (res) {
          this.showToastSuccess("Success", "Promotions updated successfully");
          this.initialPayload = JSON.parse(JSON.stringify(this.payloads));
          this.initialCashback = { ...this.cashbackPayload };
        }
      } finally {
        this.saving = false;
      }
    },
  },
  setup() {
    const { showToastSuccess } = useToastHandler();
    return { showToastSuccess };
  },
};
</script>

<style scoped>
.promo-block {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}
</style>
